import React from "react";

/**
 * Request 1222 — light / dark theme for the whole app, each person's own choice (kept in this browser). The switch is in the top
 * bar; the Management / Super Admin dashboard's own switch drives the same setting. Only the signed-in app turns dark (AppLayout
 * mounts <ApplyTheme />) — the public home and sign-in pages stay as designed.
 */
export type Theme = "light" | "dark";
const KEY = "qplus:theme";
const OLD_KEY = "qplus:mgmt-dashboard-theme"; // the dashboard's switch before 1222

function initial(): Theme {
  try {
    const v = localStorage.getItem(KEY) ?? localStorage.getItem(OLD_KEY);
    return v === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

const Ctx = React.createContext<{ theme: Theme; setTheme: (t: Theme) => void; toggle: () => void }>({ theme: "light", setTheme: () => {}, toggle: () => {} });

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setState] = React.useState<Theme>(initial);
  const setTheme = React.useCallback((t: Theme) => {
    setState(t);
    try {
      localStorage.setItem(KEY, t);
      localStorage.setItem(OLD_KEY, t);
    } catch {
      /* private mode — the choice just isn't remembered */
    }
  }, []);
  const value = React.useMemo(() => ({ theme, setTheme, toggle: () => setTheme(theme === "dark" ? "light" : "dark") }), [theme, setTheme]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useTheme = () => React.useContext(Ctx);

/** Puts the chosen theme on <html> while the signed-in app is showing. */
export function ApplyTheme() {
  const { theme } = useTheme();
  React.useEffect(() => {
    const el = document.documentElement;
    el.classList.toggle("dark", theme === "dark");
    return () => el.classList.remove("dark");
  }, [theme]);
  return null;
}
